// src/api/auth.api.js
import apiClient from './apiClient';

/**
 * Sends a request to the backend to log in a user.
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @param {string|null} mfaToken - The MFA token if available.
 * @param {object|null} locationData - Optional geolocation data.
 * @returns {Promise<object>} - A promise that resolves to the login response.
 */
export const loginUser = async (email, password, mfaToken = null, locationData = null) => {
  const response = await apiClient.post('/auth/login', { 
    email, 
    password, 
    mfaToken,
    locationData
  }, {
    headers: {
      'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  });
  return response.data;
};

/**
 * Verifies the MFA token.
 * @param {string} userId - The user's ID.
 * @param {string} token - The MFA code.
 * @returns {Promise<object>} - A promise that resolves to the verification response.
 */
export const verifyMFA = async (userId, token) => {
    const response = await apiClient.post('/mfa/verify', { userId, token });
    return response.data;
};

/**
 * Registers a new user in the system.
 * @param {object} userData - The user's registration data.
 * @returns {Promise<object>} - A promise that resolves to the newly created user data.
 */
export const registerUser = async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
};

/**
 * Sends a request to the backend to log the user out.
 */
export const logoutUser = async () => {
  try {
    await apiClient.post('/auth/logout');
  } catch (error) {
    console.warn("Logout API call failed, but proceeding with client-side logout.", error);
  }
  return { success: true };
};
